import React from 'react';
import Modal, { ModalSize } from './Modal';
import Button from './Button';

export interface HelpModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Defaults to `sm` — help is read, and a narrow measure reads faster. */
  size?: ModalSize;
  closeLabel?: string;
  /** A "Learn more" link or similar, shown beside Close. Keep it to one. */
  action?: React.ReactNode;
}

/**
 * A dialog you READ. Paired with <FormModal>, which is one you do something in.
 *
 * Deliberately lighter than FormModal in three ways:
 *
 * - Narrow by default (`sm`). Explanatory prose at a 2xl width forces the eye to
 *   track too far between lines; help gets read less when it is harder to read.
 * - `flat` elevation. It is an aside, not a task, and does not need to shout.
 * - Never dismiss-locked. There is nothing to lose, so Escape and backdrop
 *   clicks always work — the opposite of FormModal's guard.
 *
 * Body copy gets relaxed leading via `prose-help`; pass plain markup, not a card.
 * Help sits inside a dialog that is already a Level 1 surface.
 */
const HelpModal: React.FC<HelpModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  size = 'sm',
  closeLabel = 'Close',
  action,
}) => (
  <Modal
    isOpen={isOpen}
    title={title}
    onClose={onClose}
    size={size}
    elevation="flat"
    footer={
      <>
        {action}
        <Button variant="secondary" onClick={onClose}>
          {closeLabel}
        </Button>
      </>
    }
  >
    <div className="prose-help text-sm leading-relaxed text-text-secondary dark:text-text-dark-secondary">
      {children}
    </div>
  </Modal>
);

export default HelpModal;
